import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { api } from "@/lib/api";
import { useApiMutation } from "@/lib/use-api-mutation";
import {
  Link,
  Navigate,
  createFileRoute,
  useLoaderData,
  useNavigate,
} from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

/**
 * `/customers/new` — create a customer. Admin-only (accountants
 * browse + edit existing rows but don't manage the customer
 * list); non-admins are redirected to `/` which then routes
 * them to the right landing for their role.
 */

const INPUT_CLS =
  "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

export const Route = createFileRoute("/_app/customers/new")({
  component: NewCustomerPage,
});

function NewCustomerPage() {
  const { t } = useTranslation();
  const { user } = useLoaderData({ from: "/_app" });
  const navigate = useNavigate();
  const createCustomer = useApiMutation(
    (body: { name: string; fakturoidSubjectId: string | undefined }) =>
      api.post("/api/customers", body),
    {
      onSuccess: () => navigate({ to: "/customers" }),
      errorMessage: () => t("customers.new.createFailed"),
    },
  );

  if (user.globalRole !== "admin") {
    return <Navigate to="/" />;
  }

  async function create(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const name = ((fd.get("name") as string) ?? "").trim();
    if (!name) {
      createCustomer.setError(t("customers.new.nameRequired"));
      return;
    }
    await createCustomer.mutate({
      name,
      fakturoidSubjectId: ((fd.get("fakturoidSubjectId") as string) ?? "").trim() || undefined,
    });
  }

  return (
    <div className="mx-auto max-w-lg space-y-6">
      <div>
        <h1 className="text-3xl font-bold">{t("customers.new.title")}</h1>
        <p className="text-muted-foreground mt-1">{t("customers.new.subtitle")}</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t("customers.new.detailsTitle")}</CardTitle>
          <CardDescription>{t("customers.new.detailsHint")}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={create} className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="name" className="text-sm font-medium leading-none">
                {t("customers.new.name")} <span className="text-destructive">*</span>
              </label>
              <input
                id="name"
                name="name"
                type="text"
                required
                placeholder={t("customers.new.namePlaceholder")}
                className={INPUT_CLS}
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="fakturoidSubjectId" className="text-sm font-medium leading-none">
                {t("customers.new.fakturoidSubjectId")}
              </label>
              <input
                id="fakturoidSubjectId"
                name="fakturoidSubjectId"
                type="number"
                placeholder={t("customers.new.optional")}
                className={INPUT_CLS}
              />
            </div>

            {createCustomer.error && (
              <p className="text-sm text-destructive">{createCustomer.error}</p>
            )}

            <div className="flex gap-3 pt-2">
              <Button type="submit" disabled={createCustomer.pending}>
                {createCustomer.pending ? t("customers.new.creating") : t("customers.new.create")}
              </Button>
              <Button variant="outline" type="button" asChild>
                <Link to="/customers">{t("customers.new.cancel")}</Link>
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
